import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Input, message, Radio, Space } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../../api/useAPIQuery';
import Filter from '../../../components/Filter';
import { interviewScriptTemplateTypeOptions } from '../../../components/InterviewScriptTemplateForm';
import SearchParam from '../../../components/SearchParam';
import { withRouteChange } from '../../../services/APM';
import { InterviewScriptTemplateCollectionFilterSet } from '../../../types/api';
import AddInterviewScriptTemplate from './AddInterviewScriptTemplate';
import InterviewScriptTemplateTable from './InterviewScriptTemplateTable';

const InterviewScriptTemplates = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const interviewScriptTemplates = useAPIQuery([
    '/interview-script-templates/',
    {
      params: {
        type:
          (searchParams.get('type') as InterviewScriptTemplateCollectionFilterSet['type']) ??
          undefined,
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        ordering: 'name',
        ...(searchParams.get('search') ? { search: searchParams.get('search') as string } : {}),
      },
    },
  ]);

  const [isInterviewScriptTemplateOpen, setIsAddInterviewScriptTemplateOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>
          {t('Roteiros de Entrevista | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <AddInterviewScriptTemplate
        key="add-interview-script-template"
        open={isInterviewScriptTemplateOpen}
        onClose={() => setIsAddInterviewScriptTemplateOpen(false)}
        onSuccess={async () => {
          message.success(t('Roteiro adicionado.'));
          await queryClient.invalidateQueries('/interview-script-templates/');
          await queryClient.refetchQueries('/interview-script-templates/');
          setIsAddInterviewScriptTemplateOpen(false);
        }}
      />
      <Outlet />
      <Filter>
        <Col flex={1}>
          <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
            <Input.Search allowClear placeholder={t('Nome do roteiro')} />
          </SearchParam>
        </Col>
        <Filter.Item
          alwaysOn
          label={t('Tipo')}
          name="type"
          selected={
            <Filter.Selection name="type" onSetSearchParams={{ page: '1' }}>
              {interviewScriptTemplateTypeOptions.map(({ label, value }) => (
                <Filter.Tag defaultValue={value} key={value}>
                  {label}
                </Filter.Tag>
              ))}
            </Filter.Selection>
          }
        >
          <Space direction="vertical">
            <SearchParam name="type" onSetSearchParams={{ page: '1' }}>
              <Radio.Group>
                <Space direction="vertical">
                  {interviewScriptTemplateTypeOptions.map(({ label, value }) => (
                    <Radio key={value} value={value}>
                      {label}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </SearchParam>
            <SearchParam
              array
              name="type"
              onSetSearchParams={{ page: '1' }}
              valuePropName="disabledValue"
            >
              <Filter.Button>
                <Trans>Limpar</Trans>
              </Filter.Button>
            </SearchParam>
          </Space>
        </Filter.Item>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddInterviewScriptTemplateOpen(true)}
          >
            {' '}
            <Trans>Novo roteiro</Trans>
          </Button>
        </Col>
      </Filter>
      <InterviewScriptTemplateTable
        key="interview-script-template-table"
        loading={{ spinning: interviewScriptTemplates.isFetching, size: 'large' }}
        dataSource={interviewScriptTemplates.data?.results ?? []}
        pagination={{
          total: interviewScriptTemplates.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
        searchParams={searchParams}
      />
    </>
  );
};

export default withRouteChange()(InterviewScriptTemplates);
